import { Globe, Users, Shield, Zap } from "lucide-react";
import { useRegion } from "../context/RegionContext";

const indiaReasons = [
  { icon: Globe, title: "Global Presence, Local Expertise", desc: "Operations in 45+ countries with on-ground teams ensuring authentic, premium experiences backed by deep local knowledge in every destination we serve." },
  { icon: Users, title: "Built Exclusively for Travel Agents", desc: "Our platform, pricing, and support are designed exclusively for B2B travel professionals — helping you sell more, faster, and more profitably." },
  { icon: Zap, title: "Technology-First Approach", desc: "Our proprietary platform lets agents search, build, price, and book complete travel packages in minutes — replacing hours of manual work." },
  { icon: Shield, title: "Trusted & Globally Certified", desc: "ISO certified, IATA accredited, and recognized by tourism boards across Dubai, Iceland, Australia, New Zealand, and beyond." },
];

const usUkReasons = [
  { icon: Globe, title: "Local Offices, Local Experts", desc: "We operate from offices across Asia and the Middle East. Our teams live in the destinations they manage, ensuring authentic, on-the-ground expertise." },
  { icon: Users, title: "Built for Tour Operators", desc: "Our services and support are designed for travel companies who need reliable ground handling, transparent pricing, and a single point of contact." },
  { icon: Zap, title: "Complete Destination Services", desc: "From airport transfers and hotel contracting to guided tours and MICE logistics, we handle every detail so you can focus on selling." },
  { icon: Shield, title: "30 Years of Trust", desc: "ISO certified, IATA accredited, and trusted by 850+ travel partners globally. Quality standards ensured through our strong network of local experts." },
];

export default function WhyUs() {
  const { config } = useRegion();
  const isUsUk = config.code === "us" || config.code === "uk";
  const reasons = isUsUk ? usUkReasons : indiaReasons;

  return (
    <section id="why-us" data-testid="why-us-section" className="py-20 md:py-28 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        {/* Top intro */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          <div className="relative">
            <div className="rounded-2xl overflow-hidden">
              <img src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=700&q=80" alt="Hi DMC team" className="w-full h-[480px] object-cover rounded-2xl" />
            </div>
            <div className="absolute -bottom-5 -right-5 bg-[#174358] text-white rounded-xl p-6 shadow-lg">
              <p className="font-['Cormorant_Garamond'] text-4xl font-light">30+</p>
              <p className="text-xs text-white/60 mt-0.5">Years of excellence</p>
            </div>
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#174358]/50 mb-3">{isUsUk ? "The Leading International DMC" : "About Hi DMC"}</p>
            <h2 data-testid="why-us-heading" className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#0D1B2A] mb-6">
              {isUsUk ? (
                <>Why <em className="text-[#174358]">Choose</em> Hi DMC?</>
              ) : (
                <>Why <em className="text-[#174358]">Partner</em> With Us?</>
              )}
            </h2>
            <p className="text-[#0D1B2A]/55 leading-relaxed">
              {isUsUk
                ? "Hi DMC is the leading international destination management company in the tourism market. We support tour operators by offering a complete range of services in warm water destinations. Our company's strengths are our quality standards, ensured throughout our strong network of local experts across Asia, the Middle East, and Africa."
                : "Hi DMC is a global destination management company with over 30 years of experience, combining deep local expertise with cutting-edge technology. We empower travel agents with the tools, inventory, and support they need to deliver exceptional travel experiences — efficiently and profitably."}
            </p>
          </div>
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {reasons.map((r) => {
            const Icon = r.icon;
            return (
              <div key={r.title} data-testid={`why-${r.title.toLowerCase().replace(/\s+/g, '-').slice(0, 20)}`}
                className="group bg-[#F8FAFB] rounded-2xl p-8 hover:bg-white hover:shadow-lg transition-all duration-300 border border-transparent hover:border-[#174358]/08">
                <div className="w-14 h-14 rounded-2xl bg-[#174358]/8 flex items-center justify-center mb-5 group-hover:bg-[#174358] transition-colors duration-300">
                  <Icon size={24} strokeWidth={1.5} className="text-[#174358] group-hover:text-white transition-colors" />
                </div>
                <h4 className="text-lg font-bold text-[#0D1B2A] mb-2">{r.title}</h4>
                <p className="text-sm text-[#0D1B2A]/50 leading-relaxed">{r.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
